import { useMemo } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { BreadcrumbLink, Breadcrumbs } from 'components/Breadcrumbs';
import { Byline } from 'components/Byline';
import { H1 } from 'components/Text/H1';
import { CmsApiPage } from 'modules/cms/api/types/CmsApiPage';
import { CmsApiSubsite } from 'modules/cms/api/types/CmsApiSubsite';
import { CmsCategories } from 'modules/cms/components/Categories';
import { getIsContentFullWidthLayout } from 'modules/cms/helpers';
import { useCmsApiPageSubsite } from 'modules/cms/hooks';
import { cssBreakpoints } from 'theme/theme';

type Props = {
  content: CmsApiPage;
};

const BreadcrumbsContainer = styled.div`
  flex: 3;
  margin-bottom: 24px;

  @media all and (min-width: ${cssBreakpoints.smUp}) {
    flex: 1;
  }
`;

export function CmsBodyHeader({ content }: Props) {
  const fullWidthLayout = getIsContentFullWidthLayout({ content });

  const subsite = useCmsApiPageSubsite({
    cmsApiPage: content,
  }) as CmsApiSubsite;

  const links: Array<BreadcrumbLink> = useMemo(
    () => [
      {
        title: subsite.publicName,
        to: `/${subsite.homePage?.url}`,
        'data-qa-id': 'breadcrumbs-home-link',
      },
      {
        title: content.title,
        to: `/${content.url}`,
        state: {
          subsite,
        },
      },
    ],
    [subsite, content],
  );

  return (
    <>
      {content.type === 'POST' && subsite.homePage ? (
        <BreadcrumbsContainer>
          <Breadcrumbs links={links} />
        </BreadcrumbsContainer>
      ) : undefined}

      {content.type === 'POST' &&
        content.categories &&
        content.categories.length > 0 && (
          <Box mb={24}>
            <CmsCategories
              categories={content.categories}
              homePageUrl={content.subsite.homePage?.url}
            />
          </Box>
        )}

      <H1 m="0" textAlign={fullWidthLayout ? 'center' : 'left'}>
        {content.title}
      </H1>

      {(content.author || (content.postedDate && content.showDate)) && (
        <Box mt={4}>
          <Byline
            slug={content.author ? content.author.slug : undefined}
            name={content.author ? content.author.name : undefined}
            image={content.author?.image || undefined}
            homePageUrl={content.subsite.homePage?.url}
            postedDate={content.postedDate}
            showDate={content.showDate}
          />
        </Box>
      )}
    </>
  );
}
